import {
  Invitation,
  Inviter,
  Registerer,
  SessionState,
  UserAgent,
} from 'sip.js';
import VoiceAPI from './voiceAPIClient';

const createDisconnectedEvent = () => new CustomEvent('call:disconnected');

class RuutVoiceClient extends EventTarget {
  constructor() {
    super();
    this.userAgent = null;
    this.registerer = null;
    this.activeSession = null;
    this.pendingInvitation = null;
    this.inboxId = null;
    this.initialized = false;
  }

  async initializeDevice(inboxId) {
    if (this.initialized && this.inboxId === inboxId) return this.userAgent;

    await this.destroyDevice();
    const config = await VoiceAPI.getToken(inboxId);
    if (config?.provider !== 'ruut_voice')
      throw new Error('Invalid Ruut Voice session');

    this.userAgent = new UserAgent({
      uri: UserAgent.makeURI(config.aor),
      transportOptions: { server: config.websocket_url },
      authorizationUsername: config.authorization_username,
      authorizationPassword: config.authorization_password,
      displayName: config.display_name,
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: { iceServers: config.ice_servers || [] },
      },
      delegate: {
        onInvite: invitation => this.handleInvitation(invitation),
      },
    });

    this.registerer = new Registerer(this.userAgent);
    await this.userAgent.start();
    await this.registerer.register();
    this.inboxId = inboxId;
    this.initialized = true;
    return this.userAgent;
  }

  get hasActiveConnection() {
    return !!this.activeSession;
  }

  async joinClientCall() {
    const invitation = await this.waitForInvitation();
    await invitation.accept({
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    });
    this.activeSession = invitation;
    this.pendingInvitation = null;
    this.attachRemoteAudio(invitation);
    return invitation;
  }

  setMuted(shouldMute) {
    const peer = this.activeSession?.sessionDescriptionHandler?.peerConnection;
    peer?.getSenders().forEach(sender => {
      if (sender.track?.kind === 'audio') sender.track.enabled = !shouldMute;
    });
    return shouldMute;
  }

  async endClientCall() {
    const session = this.activeSession || this.pendingInvitation;
    if (session) {
      try {
        if (session.state === SessionState.Established) await session.bye();
        else if (session instanceof Inviter) await session.cancel();
        else if (session instanceof Invitation) await session.reject();
      } catch (error) {
        // The remote leg may already be gone; local cleanup still has to finish.
      }
    }
    this.activeSession = null;
    this.pendingInvitation = null;
  }

  async destroyDevice() {
    await this.endClientCall();
    try {
      if (this.registerer) await this.registerer.unregister();
      if (this.userAgent) await this.userAgent.stop();
    } catch (error) {
      // A disconnected transport is already effectively destroyed.
    }
    this.registerer = null;
    this.userAgent = null;
    this.inboxId = null;
    this.initialized = false;
  }

  handleInvitation(invitation) {
    this.pendingInvitation = invitation;
    invitation.stateChange.addListener(state => {
      if (state !== SessionState.Terminated) return;
      this.activeSession = null;
      this.pendingInvitation = null;
      this.dispatchEvent(createDisconnectedEvent());
    });
  }

  waitForInvitation() {
    if (this.pendingInvitation) return Promise.resolve(this.pendingInvitation);

    return new Promise((resolve, reject) => {
      const deadline = window.setTimeout(
        () => reject(new Error('Ruut Voice did not deliver the agent call')),
        15000
      );
      const poll = window.setInterval(() => {
        if (!this.pendingInvitation) return;
        window.clearTimeout(deadline);
        window.clearInterval(poll);
        resolve(this.pendingInvitation);
      }, 100);
    });
  }

  // Session media is attached here to keep lifecycle ownership in one client.
  // eslint-disable-next-line class-methods-use-this
  attachRemoteAudio(session) {
    const peer = session.sessionDescriptionHandler?.peerConnection;
    if (!peer) return;

    const stream = new MediaStream();
    peer.getReceivers().forEach(receiver => {
      if (receiver.track) stream.addTrack(receiver.track);
    });
    const audio = document.createElement('audio');
    audio.autoplay = true;
    audio.srcObject = stream;
    audio.play().catch(() => {});
  }
}

export default new RuutVoiceClient();
